import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { FaTimes, FaSearch, FaCalendarAlt, FaFileInvoice } from "react-icons/fa";
import api from "./api/api";

function SearchPage() {
  const navigate = useNavigate();
  const [jobList, setJobList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [docIds, setDocIds] = useState([]);
  const [docNos, setDocNos] = useState([]);
  const [idLabel, setIdLabel] = useState("DocId");
  const [noLabel, setNoLabel] = useState("DocNo");
  const [selectedJc, setSelectedJc] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  //const user = JSON.parse(localStorage.getItem("user") || "{}");
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  
  const username = user.UsrName;

  useEffect(() => {
    if (!username) navigate("/");
  }, [username, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (!username) {
          setError("No logged-in user found");
          setIsLoading(false);
          return;
        }
        const data = await api.getJobcardList(username);
        setJobList(data.Table || []);
        setInvoiceList(data.Table1 || []);
      } catch (err) {
        console.error(err);
        setError("Error loading job card list");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const jcOptions = jobList.map((job) => ({
    value: `${job.Jcid}|${job.Jcno}`,
    label: `JC: ${job.Jcno}`,
  }));

  const invoiceOptions = invoiceList.map((inv) => ({
    value: `${inv.CPId}|${inv.InvNo}`,
    label: `Invoice: ${inv.InvNo}`,
  }));

  const handleSelectJC = (value) => {
    setSelectedJc(value);
    setSelectedInvoice("");
    if (value) {
      const [id, no] = value.split("|");
      setDocIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setDocNos((prev) => (prev.includes(no) ? prev : [...prev, no]));
      setIdLabel("JcId");
      setNoLabel("JcNo");
    } else handleClear();
  };

  const handleSelectInvoice = (value) => {
    setSelectedInvoice(value);
    setSelectedJc("");
    if (value) {
      const [id, no] = value.split("|");
      setDocIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setDocNos((prev) => (prev.includes(no) ? prev : [...prev, no]));
      setIdLabel("CPId");
      setNoLabel("InvNo");
    } else handleClear();
  };

  const handleClear = () => {
    setSelectedJc("");
    setSelectedInvoice("");
    setDocIds([]);
    setDocNos([]);
    setIdLabel("DocId");
    setNoLabel("DocNo");
    setFromDate("");
    setToDate("");
    setError(null);
  };

  const handleSearch = useCallback(async () => {
    if (!docIds.length) {
      setError("Please select at least one JC No or Invoice No.");
      return;
    }
    setIsLoading(true);
    try {
      const raw = await api.getJobcardTracking(
        "user",
        noLabel,
        docIds,
        fromDate,
        toDate
      );
      const data = {
        InvoicePartDetails: raw.Table || [],
        ProductionStages: raw.Table1 || [],
        MaterialIssues: raw.Table2 || [],
        Summary: raw.Table3 || [],
      };
      navigate("/results", { state: { data } });
    } catch {
      setError("Error fetching job card tracking");
    } finally {
      setIsLoading(false);
    }
  }, [docIds, noLabel, fromDate, toDate, navigate]);

  const handleDatewiseTrace = useCallback(async () => {
    if (!fromDate || !toDate) {
      setError("Please select both From Date and To Date.");
      return;
    }
    setSelectedJc("");
    setSelectedInvoice("");
    setDocIds([]);
    setDocNos([]);
    setNoLabel("DateWise");
    setIsLoading(true);
    try {
      const raw = await api.getJobcardTracking("user", "DateWise", { fromDate, toDate });
      const data = {
        InvoicePartDetails: raw.Table || [],
        ProductionStages: raw.Table1 || [],
        MaterialIssues: raw.Table2 || [],
        Summary: raw.Table3 || [],
      };
      navigate("/results", { state: { data } });
    } catch {
      setError("Error fetching datewise job card tracking");
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, navigate]);

  const handleBackToMenu = () => {
    navigate("/Menu");
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#3B82F6" : "#CBD5E0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.3)" : "none",
      minHeight: "2.5rem",
      borderRadius: "0.5rem",
      fontSize: "0.875rem",
      "&:hover": { borderColor: "#2563EB" },
      backgroundColor: "#F3F4F6",
    }),
    menu: (provided) => ({ ...provided, borderRadius: "0.5rem", zIndex: 50 }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#DBEAFE" : "white",
      color: "#1F2937",
      padding: "8px 12px",
      fontSize: "0.875rem",
      cursor: "pointer",
    }),
    placeholder: (provided) => ({ ...provided, color: "#9CA3AF", fontSize: "0.875rem" }),
    singleValue: (provided) => ({ ...provided, color: "#1F2937", fontWeight: 500 }),
  };

  return (
    <div className="min-h-screen bg-gray-100 p-3 sm:p-6 flex justify-center items-start">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-5 sm:p-8 space-y-5 relative">
        <div className="flex items-center justify-between">
          <button
            onClick={handleBackToMenu}
            className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md flex items-center gap-1"
          >
            Back to Menu
          </button>
          <h2 className="text-2xl font-bold text-gray-900 text-center flex-1">Job Card Tracker</h2>
          <div className="w-10"></div>
        </div>
        <div>
          <label className="flex text-gray-700 text-sm mb-1 items-center gap-1">
            <FaSearch /> Job Card No
          </label>
          <Select
            options={jcOptions}
            value={jcOptions.find((opt) => opt.value === selectedJc) || null}
            onChange={(opt) => handleSelectJC(opt?.value || "")}
            isDisabled={!!selectedInvoice || !!fromDate || !!toDate}
            placeholder="Search or select JC No..."
            styles={selectStyles}
          />
        </div>

        <div>
          <label className="flex text-gray-700 text-sm mb-1 items-center gap-1">
            <FaFileInvoice /> Invoice No
          </label>
          <Select
            options={invoiceOptions}
            value={invoiceOptions.find((opt) => opt.value === selectedInvoice) || null}
            onChange={(opt) => handleSelectInvoice(opt?.value || "")}
            isDisabled={!!selectedJc || !!fromDate || !!toDate}
            placeholder="Search or select Invoice No..."
            styles={selectStyles}
          />
        </div>

        <div className="relative">
          <label className="absolute left-2 -top-2 bg-white px-1 text-gray-500 text-xs font-medium">
            {noLabel}
          </label>
          <input
            type="text"
            readOnly
            value={docNos.join(", ")}
            className="w-full border border-gray-300 rounded-md bg-gray-50 py-2 px-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Selected document numbers..."
          />
          {docNos.length > 0 && (
            <button
              onClick={handleClear}
              className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-red-600 text-lg"
            >
              <FaTimes />
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {["fromDate", "toDate"].map((type) => (
            <div className="flex-1 relative" key={type}>
              <label className="flex text-gray-700 text-sm mb-1 items-center gap-1">
                <FaCalendarAlt /> {type === "fromDate" ? "From Date" : "To Date"}
              </label>
              <input
                type="date"
                value={type === "fromDate" ? fromDate : toDate}
                onChange={(e) =>
                  type === "fromDate" ? setFromDate(e.target.value) : setToDate(e.target.value)
                }
                className="w-full border border-gray-300 rounded-md py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
              />
              <FaCalendarAlt className="absolute top-9 left-3 text-gray-400 pointer-events-none" />
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleSearch}
            disabled={isLoading || (!selectedJc && !selectedInvoice)}
            className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <FaSearch /> {isLoading ? "Tracing..." : "Trace"}
          </button>
          <button
            onClick={handleDatewiseTrace}
            disabled={isLoading || !fromDate || !toDate}
            className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <FaCalendarAlt /> {isLoading ? "Tracing..." : "Datewise Trace"}
          </button>
        </div>

        {error && <p className="text-red-600 text-sm text-center">{error}</p>}

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-xl">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
